import { useEffect, useId, useState, type ReactNode } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { t } from "@/i18n/id";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  /** Require typing this word (e.g. "HAPUS") before confirming. */
  typedWord?: string;
  loading?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({ open, onOpenChange, title, description, children, confirmLabel, destructive, typedWord, loading, onConfirm }: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  const id = useId();
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);
  const ok = !typedWord || typed.trim().toUpperCase() === typedWord;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="sm"
        title={title}
        description={description}
        footer={
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              {t.common.cancel}
            </Button>
            <Button variant={destructive ? "danger" : "primary"} disabled={!ok} loading={loading} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (ok && !loading) onConfirm();
          }}
          className="space-y-4"
        >
          {children}
          {typedWord ? (
            <Field label={t.common.typeToConfirm(typedWord)} id={id}>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder={typedWord} autoFocus />
            </Field>
          ) : null}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
